import * as React from "react"

import { cn } from "@/lib/utils"

import { AssistantAvatar } from "./assistant-avatar"
import { Card, CardContent, CardHeader } from "../../../components/ui/card"

type SystemMessageLayoutProps = React.ComponentProps<typeof Card> & {
  children?: React.ReactNode
  icon?: React.ReactNode
}

type SystemMessageLayoutContentProps = {
  children: React.ReactNode
}

export function SystemMessageLayoutContent({ children }: SystemMessageLayoutContentProps) {
  return (
    <CardContent className="flex w-full flex-col gap-6 p-3 pt-0 pb-3 lg:p-6 lg:pt-0">
      {children}
    </CardContent>
  )
}

// Helper to check if ActivityMessageHistory is present in children
function hasActivityHistory(children: React.ReactNode): boolean {
  const childrenArray = React.Children.toArray(children)

  for (const child of childrenArray) {
    if (
      !React.isValidElement<SystemMessageLayoutContentProps>(child) ||
      child.type !== SystemMessageLayoutContent
    ) {
      continue
    }

    const contentChildren = React.Children.toArray(child.props.children)
    const firstChild = contentChildren[0]

    if (!React.isValidElement<{ id?: string }>(firstChild)) {
      continue
    }

    // ActivityMessageHistory has an id prop
    if (firstChild.props.id !== undefined) {
      return true
    }

    // Also check component name as fallback
    const componentType = firstChild.type as { name?: string } | undefined
    if (componentType?.name?.includes("ActivityMessageHistory")) {
      return true
    }
  }
  return false
}

export function SystemMessageLayout({
  children,
  icon,
  className,
  ...props
}: SystemMessageLayoutProps) {
  const childrenArray = React.Children.toArray(children)
  const contentChild = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === SystemMessageLayoutContent
  )
  const otherChildren = childrenArray.filter(
    (child) => React.isValidElement(child) && child.type !== SystemMessageLayoutContent
  )

  const hasActivity = hasActivityHistory(children)

  return (
    <Card className={cn("group border-[0px] shadow-none", className)} {...props}>
      <section className="flex w-full items-start">
        <CardHeader className={cn("flex shrink-0 items-center p-0", hasActivity && "pt-3")}>
          {icon ?? (
            <div className="flex size-8 items-center justify-center">
              <AssistantAvatar />
            </div>
          )}
        </CardHeader>
        {contentChild}
      </section>
      {otherChildren}
    </Card>
  )
}

SystemMessageLayout.Content = SystemMessageLayoutContent
